// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 16-api-conventions.md
// Foot of an accumulating cursor-paginated list (see `useCursorPages`). The
// button is always rendered while more pages exist — the sentinel only fetches
// earlier, so keyboard and screen-reader users are never dependent on scroll
// position. A failed later page reports inline here, leaving the rows above it
// on screen.
import * as React from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export function LoadMore({
  hasMore,
  isLoadingMore,
  failed,
  onLoadMore,
  onRetry,
  noun,
  testId,
  className,
}: {
  hasMore: boolean;
  isLoadingMore: boolean;
  failed: boolean;
  onLoadMore: () => void;
  onRetry: () => void;
  /** Plural noun for the control's label, e.g. "grants". */
  noun: string;
  testId: string;
  className?: string;
}): React.JSX.Element | null {
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  const shouldObserve = hasMore && !isLoadingMore && !failed;

  React.useEffect(() => {
    const node = sentinelRef.current;
    // Absent in jsdom and older browsers — the button below is the fallback.
    if (!shouldObserve || node === null || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) onLoadMore();
      },
      { rootMargin: '400px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [shouldObserve, onLoadMore]);

  if (failed) {
    return (
      <div
        className={cn(
          'flex flex-wrap items-center gap-3 rounded-gl border border-border p-4',
          className,
        )}
        data-testid={`${testId}-error`}
      >
        <p className="text-sm text-muted-foreground">
          Couldn&apos;t load more {noun}. The {noun} above are still current.
        </p>
        <Button variant="outline" size="sm" onClick={onRetry} className="ml-auto">
          Retry
        </Button>
      </div>
    );
  }

  if (!hasMore) return null;

  return (
    <div className={cn('flex flex-col items-center gap-2', className)}>
      <div ref={sentinelRef} aria-hidden="true" className="h-px w-full" />
      <Button
        variant="outline"
        size="sm"
        onClick={onLoadMore}
        disabled={isLoadingMore}
        data-testid={testId}
      >
        {isLoadingMore ? 'Loading…' : `Load more ${noun}`}
      </Button>
      <span role="status" aria-live="polite" className="sr-only">
        {isLoadingMore ? `Loading more ${noun}` : ''}
      </span>
    </div>
  );
}
